import { Lineitem } from '../../interfaces/Ordel.Interface';
import { ManufacturingProcess } from '../../interfaces/Manufacturing.Interface';
import { ShopFloorTicket } from '../../interfaces/ShopFloorTicket.interface';
import { api } from '../slices/api';
import {
	getGeometryErrorMessage,
	pollGeometryOperation,
	toGeometryQueryError,
} from './geometryPoll';

// Re-exported so components can build user-facing error messages from the
// `.error` RTK Query gives back after a rotate/align/material/preview op
// fails or times out, without importing geometryPoll.ts directly.
export { getGeometryErrorMessage };

export interface BatchSibling {
	id: string;
	name: string;
	quantity: number;
	delivered_quantity: number;
	remaining_quantity: number;
	required_part_ship_date: string | null;
	required_partner_ship_date: string | null;
	status: string;
}

export interface ParamsLineItem {
	id: string;
}

export interface NewStatusOrder {
	id?: string;
	order_line_item?: string;
	status_type: string;
}

export interface KittingToolCard {
	index: number;
	tool_number: number;
	product_id: string;
	type: string;
	description: string;
	holder_vendor: string;
	holder_product_id: string;
	vendor: string;
}

export interface KittingJawCard {
	index: number;
	jaw_assembly_name: string;
	jaw_assembly_conf: string;
	base_jaws_name: string;
	cam_setup: number;
	cam_setup_total: number;
	vise: string;
}

export interface InspectionSampleLabelingState {
	required: boolean;
	sample_count: number;
	inspected_sample_numbers: number[];
}

export interface KittingOtherTag {
	key: string;
	label: string;
	description: string;
}

export interface KittingTagsData {
	order_name: string;
	line_item_name: string;
	tools: KittingToolCard[];
	jaws: KittingJawCard[];
	other_tags: KittingOtherTag[];
	/** Forms 68-70. Grouped apart from other_tags because they only appear when
	 *  the job needs a finishing, handling or cleaning process after machining. */
	special_tags: KittingOtherTag[];
	/** True when an in-progress kit already has a start user logged. */
	start_kitting_user_logged?: boolean;
}

export interface LineItemQuery {
	page?: number;
	search?: string;
	production_method?: string;
	ordering?: string;
	page_size?: number;
	status?: string;
	ballooning_status?: string;
	previously_ordered_parts?: string;
	keyence_programming_status?: string;
	quality_status?: string;
	partsbadger_production_method?: string | null;
	name?: string;
	order_name?: string;
	quantity?: string | number;
	material_state?: string;
	created_time_min?: string;
	created_time_max?: string;
	required_partner_ship_date_min?: string;
	required_partner_ship_date_max?: string;
	material_id?: string;
	building?: string | null;
	delivery_status?: string;
	machining_status?: string;
	is_queues?: boolean;
	kitting?: string;
	queue_view?: string;
	prioritization?: string;
}

// --- Async OCC geometry (PTB-6193) ---
// Shapes match what the old synchronous endpoints used to return inline, so
// resolving a poll into one of these keeps existing component render code
// (which reads e.g. `.data?.vendor_material`, `.data?.part_bbox`) working
// unchanged. Fields that used to flow through as untyped JSON (`any`) are
// deliberately kept loose here rather than pinned to e.g. `IVendorMaterial`/
// `Material` -- existing consumers (MaterialRequestModal, MaterialSelectionModal,
// ThreeDStockPreviewer) already read through these with assumptions (some
// arguably buggy, e.g. passing a `number | null` height into a `string`-typed
// setState) that predate this change; tightening the types here would ripple
// unrelated type errors into files outside this task's scope.
export interface GeometryResultResponse<T = unknown> {
	status: 'pending' | 'ready' | 'failed';
	op: string;
	result: T | null;
}

export interface SuggestedVendorMaterialResult {
	vendor_material: any;
	material_cut_length?: any;
	is_stock_rotated?: any;
}

export interface SuggestedInventoryMaterialResult {
	material: any;
	material_cut_length?: any;
	is_stock_rotated?: any;
}

export interface StockPreviewResult {
	part: any;
	stock?: any;
	jaws?: any;
	part_bbox?: { height: number; width: number; length: number };
}

// Define a service using a base URL and expected endpoints
export const extendedApiSlice = api.injectEndpoints({
	endpoints: (builder) => ({
		getOrderLineItems: builder.query<
			{
				next: string | null;
				count: number;
				results: Lineitem[];
				queue_summary?: {
					all: number;
					not_started: number;
					in_progress: number;
					late: number;
					hard_job: number;
					completed: number;
					ready: number;
				};
			},
			LineItemQuery
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();
				Object.entries(params).forEach(([key, value]) => {
					if (value !== undefined && value !== null && value !== '') {
						queryParams.append(key, value.toString());
					}
				});
				return `orders/line-items/?${queryParams.toString()}`;
			},
			providesTags: ['OrderLineItem'],
		}),
		completeOrderLineItem: builder.mutation({
			query: ({ id }: ParamsLineItem) => ({
				url: `orders/line-items/${id}/mark-as-completed/`,
				method: 'POST',
				body: {},
			}),
		}),
		createNewStatusOrderLineItem: builder.mutation({
			query: (values: NewStatusOrder) => ({
				url: 'orders/line-item-status/',
				method: 'POST',
				body: values,
			}),
		}),
		updateStatusOrderLineItem: builder.mutation({
			query: (values: NewStatusOrder) => ({
				url: `orders/line-item-status/${values.id}/`,
				method: 'PATCH',
				body: values,
			}),
		}),
		createManufacturingRoute: builder.mutation({
			query: ({ id, body }: { id?: string; body: ManufacturingProcess }) => ({
				url: `orders/line-items/${id}/manufacturing-processes/`,
				method: 'POST',
				body: body,
			}),
		}),
		syncToFusion360: builder.mutation({
			query: ({ id }: { id?: string }) => ({
				url: `orders/line-items/${id}/sync-to-fusion360/`,
				method: 'POST',
			}),
		}),
		createFusionDesign: builder.mutation({
			query: ({ id }: { id?: string }) => ({
				url: `orders/line-items/${id}/create-fusion-design/`,
				method: 'POST',
			}),
		}),
		// Async (PTB-6193): the endpoint returns 202 + a correlation_id, then the
		// actual rotation runs on the SFA's fastlane-geometry service. This
		// mutation fires the POST, then polls `GET orders/line-items/{id}/`
		// (the same pattern align-by-face/align-face-to-x-normal use, since all
		// three write their result directly onto the OLI) until
		// `geometry_status` is "ready"/"failed", resolving with the refreshed
		// line item. Callers that only cared about the promise settling (the
		// old sync endpoint returned "Success") don't need to change.
		rotatePart: builder.mutation<Lineitem, { id?: string; body: any }>({
			async queryFn({ id, body }, _queryApi, _extraOptions, baseQuery) {
				const started = await baseQuery({
					url: `orders/line-items/${id}/rotate-part/`,
					method: 'POST',
					body,
				});
				if (started.error) return { error: started.error };

				const { correlation_id } = started.data as { correlation_id: string };

				try {
					const oli = await pollGeometryOperation(
						baseQuery,
						`orders/line-items/${id}/`,
						(data) =>
							data?.geometry_correlation_id === correlation_id
								? data?.geometry_status
								: undefined,
						'Rotate part'
					);
					return { data: oli as Lineitem };
				} catch (err) {
					return { error: toGeometryQueryError(err) };
				}
			},
		}),
		alignPartByFace: builder.mutation<
			Lineitem,
			{ id: string; body: { id: number } }
		>({
			async queryFn({ id, body }, _queryApi, _extraOptions, baseQuery) {
				const started = await baseQuery({
					url: `orders/line-items/${id}/align-by-face/`,
					method: 'POST',
					body,
				});
				if (started.error) return { error: started.error };

				const { correlation_id } = started.data as { correlation_id: string };

				try {
					const oli = await pollGeometryOperation(
						baseQuery,
						`orders/line-items/${id}/`,
						(data) =>
							data?.geometry_correlation_id === correlation_id
								? data?.geometry_status
								: undefined,
						'Align part by face'
					);
					return { data: oli as Lineitem };
				} catch (err) {
					return { error: toGeometryQueryError(err) };
				}
			},
		}),
		alignPartToXAxisByFace: builder.mutation<
			Lineitem,
			{ id: string; body: { id: number } }
		>({
			async queryFn({ id, body }, _queryApi, _extraOptions, baseQuery) {
				const started = await baseQuery({
					url: `orders/line-items/${id}/align-face-to-x-normal/`,
					method: 'POST',
					body,
				});
				if (started.error) return { error: started.error };

				const { correlation_id } = started.data as { correlation_id: string };

				try {
					const oli = await pollGeometryOperation(
						baseQuery,
						`orders/line-items/${id}/`,
						(data) =>
							data?.geometry_correlation_id === correlation_id
								? data?.geometry_status
								: undefined,
						'Align part to X-axis'
					);
					return { data: oli as Lineitem };
				} catch (err) {
					return { error: toGeometryQueryError(err) };
				}
			},
		}),
		// Async (PTB-6193): GET returns 202 + a correlation_id; the DB match
		// (vendor material fit against the SFA's oriented bbox) runs in the
		// background and is stashed on a GeometryResult row. Poll
		// `geometry-result/{correlation_id}/` until it's ready, then resolve
		// with `.result`, which has the exact same shape
		// (`{vendor_material, material_cut_length, is_stock_rotated}`) the old
		// synchronous endpoint returned inline.
		getSuggestedVendorMaterialBasedOnPartOrientation: builder.query<
			SuggestedVendorMaterialResult,
			{
				id?: string;
				shape?: string;
				types?: string | string[];
				allow_rotated_stock?: boolean;
			}
		>({
			async queryFn(
				{ id, shape, types, allow_rotated_stock },
				_queryApi,
				_extraOptions,
				baseQuery
			) {
				const started = await baseQuery({
					url: `orders/line-items/${id}/suggested-vendor-material-based-on-part-orientation/`,
					params: { shape, types, allow_rotated_stock },
				} as any);
				if (started.error) return { error: started.error };

				const { correlation_id } = started.data as { correlation_id: string };

				try {
					const final = (await pollGeometryOperation(
						baseQuery,
						`orders/line-items/${id}/geometry-result/${correlation_id}/`,
						(data) => data?.status,
						'Suggested vendor material lookup',
						{ pendingOn404: true }
					)) as GeometryResultResponse<SuggestedVendorMaterialResult>;
					return {
						data: final.result ?? { vendor_material: null },
					};
				} catch (err) {
					return { error: toGeometryQueryError(err) };
				}
			},
		}),
		getSuggestedInventoryMaterialOnPartOrientation: builder.query<
			SuggestedInventoryMaterialResult,
			{ id?: string; shape?: string; types?: string | string[] }
		>({
			async queryFn({ id, shape, types }, _queryApi, _extraOptions, baseQuery) {
				const started = await baseQuery({
					url: `orders/line-items/${id}/suggested-inventory-material-based-on-part-orientation/`,
					params: { shape, types },
				} as any);
				if (started.error) return { error: started.error };

				const { correlation_id } = started.data as { correlation_id: string };

				try {
					const final = (await pollGeometryOperation(
						baseQuery,
						`orders/line-items/${id}/geometry-result/${correlation_id}/`,
						(data) => data?.status,
						'Suggested inventory material lookup',
						{ pendingOn404: true }
					)) as GeometryResultResponse<SuggestedInventoryMaterialResult>;
					return {
						data: final.result ?? { material: null },
					};
				} catch (err) {
					return { error: toGeometryQueryError(err) };
				}
			},
		}),
		// Async (PTB-6193): GET returns 202 + a correlation_id; the Three.js
		// preview payload is rendered on the SFA and stashed on a
		// GeometryResult row. Poll `geometry-result/{correlation_id}/` until
		// ready, then remap the SFA's `part_faces` key back to `part` -- the
		// key the old synchronous endpoint (and every consumer of this query,
		// e.g. ThreeDStockSizePreviewer's `partBufferGeometry` prop) uses.
		getStockPreview: builder.query<
			StockPreviewResult,
			{ id?: string; params: any }
		>({
			async queryFn({ id, params }, _queryApi, _extraOptions, baseQuery) {
				const started = await baseQuery({
					url: `orders/line-items/${id}/preview-stock/`,
					params,
				} as any);
				if (started.error) return { error: started.error };

				const { correlation_id } = started.data as { correlation_id: string };

				try {
					const final = (await pollGeometryOperation(
						baseQuery,
						`orders/line-items/${id}/geometry-result/${correlation_id}/`,
						(data) => data?.status,
						'Stock preview',
						{ pendingOn404: true }
					)) as GeometryResultResponse<{
						part_faces?: unknown;
						stock?: unknown;
						jaws?: unknown;
						part_bbox?: { height: number; width: number; length: number };
					}>;
					const result = final.result ?? {};
					return {
						data: {
							part: result.part_faces,
							stock: result.stock,
							jaws: result.jaws,
							part_bbox: result.part_bbox,
						},
					};
				} catch (err) {
					return { error: toGeometryQueryError(err) };
				}
			},
		}),
		// Raw poll endpoint, exposed directly for callers that want to manage
		// their own polling/UI (e.g. showing op-specific progress) instead of
		// consuming the pre-polled mutations/queries above.
		getGeometryResult: builder.query<
			GeometryResultResponse,
			{ id: string; correlationId: string }
		>({
			query: ({ id, correlationId }) =>
				`orders/line-items/${id}/geometry-result/${correlationId}/`,
		}),
		// Async (PTB-6193): by-dimensions sibling of getStockPreview above --
		// same SFA op ("preview_stock"), so the same poll/remap
		// (part_faces -> part) applies; see that endpoint's comment for the
		// full rationale.
		getStockPreviewByDimensions: builder.query<
			StockPreviewResult,
			{ id?: string; params: any }
		>({
			async queryFn({ id, params }, _queryApi, _extraOptions, baseQuery) {
				const started = await baseQuery({
					url: `orders/line-items/${id}/preview-stock-by-dimensions/`,
					params,
				} as any);
				if (started.error) return { error: started.error };

				const { correlation_id } = started.data as { correlation_id: string };

				try {
					const final = (await pollGeometryOperation(
						baseQuery,
						`orders/line-items/${id}/geometry-result/${correlation_id}/`,
						(data) => data?.status,
						'Stock preview',
						{ pendingOn404: true }
					)) as GeometryResultResponse<{
						part_faces?: unknown;
						stock?: unknown;
						jaws?: unknown;
						part_bbox?: { height: number; width: number; length: number };
					}>;
					const result = final.result ?? {};
					return {
						data: {
							part: result.part_faces,
							stock: result.stock,
							jaws: result.jaws,
							part_bbox: result.part_bbox,
						},
					};
				} catch (err) {
					return { error: toGeometryQueryError(err) };
				}
			},
		}),
		getGroupedRelatedMaterialReservations: builder.query({
			query: ({ id }) =>
				`orders/line-items/${id}/material-reservations-grouped`,
		}),
		startKitting: builder.mutation({
			query: ({
				id,
				building_id,
				operator_user_id,
				operator_pin,
			}: {
				id: string;
				building_id?: string;
				operator_user_id?: number;
				operator_pin?: string;
			}) => ({
				url: `orders/line-items/${id}/start-kitting/`,
				method: 'POST',
				body: {
					...(building_id ? { building_id } : {}),
					...(operator_user_id ? { operator_user_id } : {}),
					...(operator_pin ? { operator_pin } : {}),
				},
			}),
		}),
		completeKitting: builder.mutation({
			query: ({
				id,
				building_id,
				operator_user_id,
				operator_pin,
			}: {
				id: string;
				building_id?: string;
				operator_user_id?: number;
				operator_pin?: string;
			}) => ({
				url: `orders/line-items/${id}/complete-kitting/`,
				method: 'POST',
				body: {
					...(building_id ? { building_id } : {}),
					...(operator_user_id ? { operator_user_id } : {}),
					...(operator_pin ? { operator_pin } : {}),
				},
			}),
		}),
		getKittingTagsData: builder.query<KittingTagsData, string>({
			query: (id) => `orders/line-items/${id}/kitting-tags-data/`,
		}),
		printSelectedKittingTags: builder.mutation({
			query: ({
				id,
				tool_indices,
				jaw_indices,
				other_tag_keys,
				building_id,
				operator_user_id,
				operator_pin,
			}: {
				id: string;
				tool_indices: number[];
				jaw_indices: number[];
				other_tag_keys?: string[];
				building_id?: string;
				operator_user_id?: number;
				operator_pin?: string;
			}) => ({
				url: `orders/line-items/${id}/print-selected-kitting-tags/`,
				method: 'POST',
				body: {
					tool_indices,
					jaw_indices,
					other_tag_keys,
					building_id,
					...(operator_user_id ? { operator_user_id } : {}),
					...(operator_pin ? { operator_pin } : {}),
				},
			}),
		}),
		previewSelectedKittingTags: builder.mutation<
			Blob,
			{
				id: string;
				tool_indices: number[];
				jaw_indices: number[];
				other_tag_keys?: string[];
			}
		>({
			query: ({ id, tool_indices, jaw_indices, other_tag_keys }) => ({
				url: `orders/line-items/${id}/preview-selected-kitting-tags/`,
				method: 'POST',
				body: { tool_indices, jaw_indices, other_tag_keys },
				responseHandler: async (response) => {
					const blob = await response.blob();
					return blob;
				},
			}),
		}),
		generateProductionTravelerReport: builder.query<
			Blob,
			{ id: string; force?: boolean }
		>({
			query: ({ id, force }) => ({
				url: `orders/line-items/${id}/generate-production-traveler-report/`,
				params: force ? { force: 'true' } : undefined,
				responseHandler: async (response) => {
					const blob = await response.blob();
					return blob;
				},
				transformResponse: (response: Blob) => response,
			}),
		}),
		generateFaiReport: builder.query<Blob, string>({
			query: (id) => ({
				url: `orders/line-items/${id}/generate-fai-report/`,
				responseHandler: async (response) => {
					const blob = await response.blob();
					return blob;
				},
				transformResponse: (response: Blob) => response,
			}),
		}),
		exportCsv: builder.query<Blob, string>({
			query: (id) => ({
				url: `orders/line-items/${id}/export-csv/`,
				responseHandler: async (response) => await response.blob(),
			}),
		}),

		// Queue background generation for ballooned PDF
		queueGenerateBalloonedPdf: builder.mutation<
			{ task_id: string },
			{ id: string }
		>({
			query: ({ id }) => ({
				url: `orders/line-items/${id}/queue-generate-ballooned-pdf/`,
				method: 'POST',
				body: {},
			}),
		}),

		// Poll task status by task_id
		getBallooningPdfTaskStatus: builder.query<
			{ finished: boolean; state: string; message?: string },
			{ task_id: string }
		>({
			query: ({ task_id }) => ({
				url: `orders/line-items/ballooning-pdf-task-status/`,
				params: { task_id },
			}),
		}),
		generateBalloonedPdf: builder.query<Blob, string>({
			query: (id) => ({
				url: `orders/line-items/${id}/generate-ballooned-pdf/`,
				responseHandler: async (response) => await response.blob(),
			}),
		}),
		generateBalloonedFAIPdf: builder.query<Blob, string>({
			query: (id) => ({
				url: `orders/line-items/${id}/generate-ballooned-fai-pdf/`,
				responseHandler: async (response) => await response.blob(),
			}),
		}),
		mergeMaterialCertificates: builder.query<Blob, string>({
			query: (id) => ({
				url: `orders/line-items/${id}/merge-material-certificates/`,
				responseHandler: async (response) => await response.blob(),
			}),
		}),
		generateQualityPacket: builder.query<Blob, { id: string; force?: boolean }>(
			{
				query: ({ id, force }) => ({
					url: `orders/line-items/${id}/generate-quality-packet/`,
					params: force ? { force: 'true' } : undefined,
					responseHandler: async (response) => await response.blob(),
				}),
			}
		),
		uploadFaiCsv: builder.mutation<
			ResponseType,
			{ id: string; file: FormData }
		>({
			query: ({ id, file }) => ({
				url: `orders/line-items/${id}/upload-csv/`,
				method: 'POST',
				body: file,
			}),
		}),

		createDelivery: builder.mutation({
			query: ({ id, params }) => ({
				url: `orders/line-items/${id}/create-delivery/`,
				method: 'POST',
				body: params,
			}),
		}),

		getBatchSiblings: builder.query<
			{ siblings: BatchSibling[] },
			{ id: string }
		>({
			query: ({ id }) => `orders/line-items/${id}/batch-siblings/`,
		}),

		reassignDelivery: builder.mutation<
			{ id: string; order_line_item_id: string },
			{
				id: string;
				delivery_id: string;
				target_line_item_id: string;
				cleaning_instructions_acknowledged?: boolean;
			}
		>({
			query: ({ id, ...body }) => ({
				url: `orders/line-items/${id}/reassign-delivery/`,
				method: 'POST',
				body,
			}),
		}),

		getAllDeliveryByOrderItem: builder.query<
			{
				count: number;
				results: Array<Record<string, unknown>>;
				next: string | null;
				truncated: boolean;
			},
			{ id: string }
		>({
			// The API pages at 20. Walk `next` so the table still shows every
			// row without one response carrying the whole history. Stop at
			// 200 pages (4,000 rows) and say so, instead of pretending the
			// list is complete.
			async queryFn({ id }, _api, _extraOptions, baseQuery) {
				const MAX_PAGES = 200;
				const results: Array<Record<string, unknown>> = [];
				let page = 1;
				let count = 0;
				let next: string | null = 'start';
				let truncated = false;
				while (next) {
					if (page > MAX_PAGES) {
						truncated = true;
						break;
					}
					const response = await baseQuery(
						`orders/line-items/${id}/deliveries/?page=${page}`
					);
					if (response.error) return { error: response.error };
					const body = (response.data ?? {}) as {
						count?: number;
						next?: string | null;
						results?: Array<Record<string, unknown>>;
					};
					if (page === 1) count = body.count ?? 0;
					results.push(...(body.results ?? []));
					next = body.next ?? null;
					page += 1;
				}
				return {
					data: {
						count,
						results,
						next: truncated ? next : null,
						truncated,
					},
				};
			},
		}),

		// Samples the operator must confirm are bagged and labeled before the
		// delivery tag prints (PTB-6619).
		getInspectionSampleLabeling: builder.query<
			InspectionSampleLabelingState,
			{ id: string }
		>({
			query: ({ id }) => `orders/line-items/${id}/inspection-sample-labeling/`,
		}),

		printDeliveryById: builder.mutation({
			query: ({
				order_item_id,
				delivery_id,
				building_id,
			}: {
				order_item_id: string;
				delivery_id: string;
				building_id?: string;
			}) => ({
				url: `orders/line-items/${order_item_id}/print-delivery/?id=${delivery_id}`,
				method: 'POST',
				body: building_id ? { building_id } : undefined,
			}),
		}),

		syncToVendorApp: builder.mutation<
			{ message: string },
			{
				id: string;
				document_type?: 'First Article Inspection' | 'Material Certificate';
			}
		>({
			query: ({ id, document_type }) => ({
				url: `orders/line-items/${id}/sync-to-vendor-app/`,
				method: 'POST',
				body: document_type ? { document_type } : {},
			}),
		}),

		getQueueTotal: builder.query({
			query: () =>
				`orders/line-items/queue_counts/?is_queues=true&created_time_min=2025-03-01`,
		}),

		getOrderLineItemDocuments: builder.query<any[], string>({
			query: (id) => `orders/line-items/${id}/documents/`,
		}),

		uploadOrderLineItemDocuments: builder.mutation<
			any,
			{ id: string; formData: FormData }
		>({
			query: ({ id, formData }) => ({
				url: `orders/line-items/${id}/documents/`,
				method: 'POST',
				body: formData,
			}),
		}),

		deleteOrderLineItemDocument: builder.mutation<
			void,
			{ id: string; fileId: string }
		>({
			query: ({ id, fileId }) => ({
				url: `orders/line-items/${id}/documents/${fileId}/`,
				method: 'DELETE',
			}),
		}),

		copyOrderLineItemDocuments: builder.mutation<
			any,
			{ document_ids: string[]; target_line_item_ids: string[] }
		>({
			query: (body) => ({
				url: `orders/line-items/documents/copy/`,
				method: 'POST',
				body,
			}),
		}),

		getOrderLineItemNotes: builder.query<any[], string>({
			query: (id) => `orders/line-items/${id}/notes/`,
		}),

		/**
		 * Shop-floor support tickets raised against this job, whole timelines
		 * included. Read-only: they are raised, replied to and resolved in the
		 * Shop Floor App, and the ERP holds a mirror of them.
		 */
		getOrderLineItemTickets: builder.query<ShopFloorTicket[], string>({
			query: (id) => `orders/line-items/${id}/tickets/`,
		}),

		addOrderLineItemNote: builder.mutation<any, { id: string; text: string }>({
			query: ({ id, text }) => ({
				url: `orders/line-items/${id}/notes/`,
				method: 'POST',
				body: { text },
			}),
		}),

		deleteOrderLineItemNote: builder.mutation<
			void,
			{ id: string; noteId: string }
		>({
			query: ({ id, noteId }) => ({
				url: `orders/line-items/${id}/notes/${noteId}/`,
				method: 'DELETE',
			}),
		}),

		copyOrderLineItemNotes: builder.mutation<
			any,
			{ note_ids: string[]; target_line_item_ids: string[] }
		>({
			query: (body) => ({
				url: `orders/line-items/notes/copy/`,
				method: 'POST',
				body,
			}),
		}),

		getRelatedMaterialReservations: builder.query<any, string>({
			query: (id) => `orders/line-items/${id}/material-reservations/`,
		}),
	}),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
	useGetOrderLineItemsQuery,
	useCompleteOrderLineItemMutation,
	useCreateNewStatusOrderLineItemMutation,
	useUpdateStatusOrderLineItemMutation,
	useCreateManufacturingRouteMutation,
	useLazyGetOrderLineItemsQuery,
	useSyncToFusion360Mutation,
	useCreateFusionDesignMutation,
	useRotatePartMutation,
	useAlignPartByFaceMutation,
	useAlignPartToXAxisByFaceMutation,
	useGetSuggestedVendorMaterialBasedOnPartOrientationQuery,
	useGetSuggestedInventoryMaterialOnPartOrientationQuery,
	useLazyGetSuggestedInventoryMaterialOnPartOrientationQuery,
	useGetStockPreviewQuery,
	useLazyGetStockPreviewQuery,
	useGetStockPreviewByDimensionsQuery,
	useLazyGetStockPreviewByDimensionsQuery,
	useGetGeometryResultQuery,
	useLazyGetGeometryResultQuery,
	useGetGroupedRelatedMaterialReservationsQuery,
	useLazyGetGroupedRelatedMaterialReservationsQuery,
	useStartKittingMutation,
	useCompleteKittingMutation,
	useGetKittingTagsDataQuery,
	useLazyGetKittingTagsDataQuery,
	usePrintSelectedKittingTagsMutation,
	usePreviewSelectedKittingTagsMutation,
	useGenerateFaiReportQuery,
	useLazyExportCsvQuery,
	useLazyGenerateProductionTravelerReportQuery,
	useLazyGenerateFaiReportQuery,
	useUploadFaiCsvMutation,
	useLazyGenerateBalloonedPdfQuery,
	useLazyGenerateBalloonedFAIPdfQuery,
	useLazyMergeMaterialCertificatesQuery,
	useLazyGenerateQualityPacketQuery,
	useLazyGetQueueTotalQuery,
	useGetOrderLineItemDocumentsQuery,
	useUploadOrderLineItemDocumentsMutation,
	useDeleteOrderLineItemDocumentMutation,
	useCopyOrderLineItemDocumentsMutation,
	useGetOrderLineItemNotesQuery,
	useGetOrderLineItemTicketsQuery,
	useAddOrderLineItemNoteMutation,
	useDeleteOrderLineItemNoteMutation,
	useCopyOrderLineItemNotesMutation,
	useQueueGenerateBalloonedPdfMutation,
	useGetBallooningPdfTaskStatusQuery,
	useGetRelatedMaterialReservationsQuery,
	// Delivery Actions
	useCreateDeliveryMutation,
	useGetBatchSiblingsQuery,
	useReassignDeliveryMutation,
	useGetAllDeliveryByOrderItemQuery,
	useLazyGetInspectionSampleLabelingQuery,
	usePrintDeliveryByIdMutation,
	useSyncToVendorAppMutation,
} = extendedApiSlice;
